/* component-media-gallery.js — <media-gallery>
   PDP media gallery: a scroll-snap slide track + a thumbnail strip that stay
   in sync, plus variant-driven jumps and variant-driven media FILTERING
   (native replacement for live's Variant Image Wizard app).

   Markup (rendered by main-product.liquid):
     <media-gallery data-section-id="{{ section.id }}">
       <div data-gallery-viewport>
         <div data-gallery-track>
           <div data-gallery-slide data-media-id="123" data-media-type="image"
                data-media-alt="...">...</div>
         </div>
         <button data-gallery-prev>…</button>
         <button data-gallery-next>…</button>
       </div>
       <div class="s-pdp__thumbs-wrap">           <!-- only when media.size > 1 -->
         <div data-gallery-thumbs>
           <button data-gallery-thumb data-media-id="123">…</button>
         </div>
         <button data-gallery-thumbs-next>…</button>
       </div>
     </media-gallery>

   Behaviour:
     - Click a thumb → scroll matching slide into view, mark it active.
     - Scroll the track → active thumb follows (IntersectionObserver).
     - prev / next buttons step one slide.
     - Subscribes kk:variant:changed: filters media to the selection, then jumps
       to the variant's featured_media when that slide is (still) visible.
       Jumps scroll ONLY the horizontal track — never the page (89% mobile;
       a document scrollIntoView would yank the viewport off the buy box).

   Variant filtering (owner: "80x80 shows only 80x80 product + lifestyle images"):
     Each image is TAGGED by matching its ALT text AND its file name against the
     product's real option values (read from the variant-picker inputs in the
     same .s-pdp section):
       - Size values ("80 x 80 cm", "2er Set 50 x 70 cm") match via canonical
         dimension tokens — digitsxdigits pairs, orientation-insensitive, so a
         "…_70x50-03.jpg" file matches the "50 x 70 cm" value. Tokens that equal
         no option value (e.g. a 700x700 resolution marker in a file name) are
         ignored entirely.
       - Other values (colours, cover types) match as whole words inside the
         ALT + file-name text. Text is normalised with German transliteration
         (ß→ss, ä→ae, ö→oe, ü→ue) and separators folded to spaces, so value
         "Weiß" also matches a "KK_weiss-01.jpg" file. When a MORE specific
         value matches the same text ("Weiß-Grau"), its components ("Weiß",
         "Grau") are pruned so compound-colour images only show under the
         compound value.
     Images that match NO option value are generic/lifestyle → always visible.
     An option position only discriminates when the SELECTED value has at least
     one tagged image (a size that has no dedicated photos shows the full
     gallery instead of stranding the shopper on generic shots). A tagged image
     is visible only when, for every discriminating position it is tagged on,
     the selected value is among its tags. Videos/3D models are always visible.

   Pure DOM/scroll; no external deps. Degrades to a plain scroller with no JS. */

// German→English aliases: the catalog's image FILES are mostly named in English
// (KK-Cotton-Blue, Bamboo-Grey, Microplush-Green, Haki_1) while option values are
// German. Each alias is matched as a whole word, so "blue" never fires inside
// "skyblue". Keys are post-transliteration norms (ü→ue, ß→ss).
const KK_VALUE_ALIASES = {
  weiss: ['white'], schwarz: ['black'], grau: ['grey', 'gray'],
  dunkelgrau: ['darkgrey', 'darkgray', 'darkgrau'],
  hellblau: ['skyblue', 'lightblue'], blau: ['blue'], gruen: ['green'],
  braun: ['brown'], rosa: ['pink'], pfirsich: ['peach'],
  wolken: ['clouds', 'cloud'], blume: ['flower', 'floral'],
  walnuss: ['walnut'], khaki: ['haki'],
  bambus: ['bamboo'], baumwolle: ['cotton'], mikropluesch: ['microplush', 'minky'],
  baer: ['bear'], elefant: ['elephant'], frosch: ['frog'],
  // Kids designs + bedding (audit-verified against real file names; himmelblau→skyblue
  // deliberately ABSENT — the body pillow carries BOTH Hellblau and Himmelblau).
  einhorn: ['unicorn'], hai: ['shark'], fuchs: ['fox'], loewe: ['lion', 'lowe'],
  dino: ['dinosaur'], bunt: ['colorful'], hellbeige: ['beige'],
  weltraum: ['planet'], regenbogen: ['rainbow'],
};

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');
    this.thumbsStrip = this.querySelector('[data-gallery-thumbs]');
    this.thumbsNext = this.querySelector('[data-gallery-thumbs-next]');
    this.thumbsPrev = this.querySelector('[data-gallery-thumbs-prev]');
    this.sectionId = this.dataset.sectionId;
    this._active = 0;
    const ci = parseInt(this.dataset.colorOptionIndex, 10);
    this._colorIndex = Number.isNaN(ci) ? -1 : ci;
    this._initialColor = this.dataset.initialColor || '';
    if (!this.track || this.slides.length === 0) return;

    // Duplicate-upload dedupe (owner: "remove duplicated images from product
    // gallery"): re-uploading a file makes Shopify append a hex GUID to the same
    // basename (Main_Image_KK-Cotton-Clouds.jpg vs main_image_kk-cotton-clouds_798de48b….jpg).
    // Hide every slide whose normalised basename already appeared — data stays
    // untouched, the gallery just never shows the copy.
    const seenFiles = {};
    const dupes = [];
    this.slides.forEach((s, i) => {
      const img = s.querySelector('img');
      if (!img || (s.getAttribute('data-media-type') || 'image') !== 'image') return;
      let base = ((img.getAttribute('src') || '').split('?')[0].split('/').pop() || '').toLowerCase();
      base = base.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\.)/, '');
      if (!base) return;
      // 2026-09-10: AI renders uploaded in one batch share the stamp basename (hf_20260910_115328_<guid>.png) and
      // were all pruned as "copies" (EN gallery showed 1 of 5). A copy is only a copy when the ALT matches too.
      const key = base + '|' + (s.getAttribute('data-media-alt') || '').trim().toLowerCase();
      if (seenFiles[key]) dupes.push(i);
      else seenFiles[key] = true;
    });
    for (let i = dupes.length - 1; i >= 0; i--) {
      const idx = dupes[i];
      const t = this.thumbs[idx];
      this.slides[idx].remove();
      if (t) t.remove();
      this.slides.splice(idx, 1);
      if (t) this.thumbs.splice(idx, 1);
    }

    // Tag every slide once against the product's option values (perf: 89% mobile).
    this._values = this._knownValues();
    // No picker inputs in the DOM (variant_picker block removed): fall back to the
    // server-embedded initial colour so the legacy attribute contract still works.
    if (!this._values.length && this._colorIndex >= 0 && this._initialColor) {
      const n = this._norm(this._initialColor);
      this._values = [{ norm: n, dims: this._dims(this._initialColor), pos: this._colorIndex + 1, terms: [n] }];
    }
    this._slideTags = this.slides.map((s) => this._tagsFor(s));

    // Thumbnail strip "next" arrow: scroll a page of thumbs into view; hide when the
    // strip does not overflow (nothing to scroll to).
    if (this.thumbsNext && this.thumbsStrip) {
      this._syncThumbsNext = () => {
        const overflow = this.thumbsStrip.scrollWidth - this.thumbsStrip.clientWidth > 4;
        const atEnd = this.thumbsStrip.scrollLeft >= (this.thumbsStrip.scrollWidth - this.thumbsStrip.clientWidth - 4);
        this.thumbsNext.hidden = !overflow || atEnd;
        // 2026-09-16 (Jonas): matching "previous" arrow once the strip has been scrolled.
        if (this.thumbsPrev) this.thumbsPrev.hidden = !overflow || this.thumbsStrip.scrollLeft <= 4;
      };
      this._onThumbsNext = () => {
        this.thumbsStrip.scrollBy({ left: Math.round(this.thumbsStrip.clientWidth * 0.8), behavior: 'smooth' });
      };
      this.thumbsNext.addEventListener('click', this._onThumbsNext);
      if (this.thumbsPrev) {
        this._onThumbsPrev = () => { var target = this.thumbsStrip.scrollLeft - Math.round(this.thumbsStrip.clientWidth * 0.8); this.thumbsStrip.scrollTo({ left: target < 40 ? 0 : target, behavior: 'smooth' }); };
        this.thumbsPrev.addEventListener('click', this._onThumbsPrev);
      }
      this.thumbsStrip.addEventListener('scroll', this._syncThumbsNext, { passive: true });
      window.addEventListener('resize', this._syncThumbsNext);
      this._syncThumbsNext();
    }

    this._onThumb = (e) => {
      const btn = e.currentTarget;
      const id = btn.getAttribute('data-media-id');
      this.goToMediaId(id);
    };
    this.thumbs.forEach((t) => t.addEventListener('click', this._onThumb));

    if (this.prevBtn) { this._onPrev = () => this.step(-1); this.prevBtn.addEventListener('click', this._onPrev); }
    if (this.nextBtn) { this._onNext = () => this.step(1); this.nextBtn.addEventListener('click', this._onNext); }

    // Observe which slide is centred → sync active thumb.
    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = this.slides.indexOf(entry.target);
            if (idx > -1) this._setActive(idx, false);
          }
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((s) => this._io.observe(s));
    }

    // Variant change: filter, then jump to the variant's featured media if that
    // slide survived the filter (goToMediaId ignores hidden slides).
    if (window.KK && window.KK.pubsub) {
      this._unsub = window.KK.pubsub.subscribe('kk:variant:changed', (p) => {
        if (!p || p.sectionId !== this.sectionId) return;
        const v = p.variant;
        const opts = (v && v.options) ? v.options : [];
        const byPos = {};
        opts.forEach((val, i) => { if (val) byPos[i + 1] = this._norm(val); });
        this._applyVariantFilter(byPos);
        const mediaId = v && v.featured_media && (v.featured_media.id != null ? v.featured_media.id : v.featured_media);
        if (mediaId != null) this.goToMediaId(String(mediaId));
      });
    }

    this._setActive(0, false);

    // Initial load: filter to the picker's server-checked selection so a
    // ?variant= deep link (or default variant) lands on a filtered gallery.
    const initial = this._selectedByPosFromDOM();
    if (Object.keys(initial).length) {
      this._applyVariantFilter(initial);
    } else if (this._colorIndex >= 0 && this._initialColor) {
      const byPos = {};
      byPos[this._colorIndex + 1] = this._norm(this._initialColor);
      this._applyVariantFilter(byPos);
    }
  }

  disconnectedCallback() {
    this.thumbs.forEach((t) => t.removeEventListener('click', this._onThumb));
    if (this.prevBtn && this._onPrev) this.prevBtn.removeEventListener('click', this._onPrev);
    if (this.nextBtn && this._onNext) this.nextBtn.removeEventListener('click', this._onNext);
    if (this.thumbsNext && this._onThumbsNext) this.thumbsNext.removeEventListener('click', this._onThumbsNext);
    if (this.thumbsPrev && this._onThumbsPrev) this.thumbsPrev.removeEventListener('click', this._onThumbsPrev);
    if (this.thumbsStrip && this._syncThumbsNext) this.thumbsStrip.removeEventListener('scroll', this._syncThumbsNext);
    if (this._syncThumbsNext) window.removeEventListener('resize', this._syncThumbsNext);
    if (this._io) this._io.disconnect();
    if (this._unsub) this._unsub();
  }

  goToMediaId(id) {
    const idx = this.slides.findIndex((s) => s.getAttribute('data-media-id') === String(id));
    if (idx > -1 && !this.slides[idx].hidden) this._setActive(idx, true);
  }

  // Indexes (into the full slides array) of slides currently shown.
  _visibleIndexes() {
    const out = [];
    for (let i = 0; i < this.slides.length; i++) if (!this.slides[i].hidden) out.push(i);
    return out;
  }

  step(dir) {
    const visible = this._visibleIndexes();
    if (!visible.length) return;
    let pos = visible.indexOf(this._active);
    if (pos < 0) pos = 0;
    pos = Math.max(0, Math.min(visible.length - 1, pos + dir));
    this._setActive(visible[pos], true);
  }

  _setActive(idx, scroll) {
    this._active = idx;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
    this.thumbs.forEach((t, i) => {
      const on = i === idx;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-current', on ? 'true' : 'false');
    });
    if (scroll && this.slides[idx] && this.track) {
      // Scroll the TRACK only. scrollIntoView would also scroll the document —
      // on mobile every colour tap would yank the viewport off the buy box.
      const left = this.slides[idx].offsetLeft - (this.slides[0] ? this.slides[0].offsetLeft : 0);
      this.track.scrollTo({ left: left, behavior: 'smooth' });
    }
    // Leaving a slide pauses its video; navigating TO a video slide (thumb click,
    // arrows — scroll=true paths only, so swipes/initial render never pull the
    // file early) loads and autoplays it muted (owner: "click on the gallery
    // video → it should automatically load and play").
    this.slides.forEach((s, i) => {
      if (i === idx) return;
      const v = s.querySelector('video');
      if (v && !v.paused) v.pause();
    });
    if (scroll && this.slides[idx]) {
      const dm = this.slides[idx].querySelector('deferred-media[data-media-type="video"]');
      if (dm && typeof dm.load === 'function') dm.load();
      const v = this.slides[idx].querySelector('video');
      if (v && v.paused) v.play().catch(() => {});
    }
    const visible = this._visibleIndexes();
    const vpos = visible.indexOf(idx);
    if (this.prevBtn) this.prevBtn.disabled = vpos <= 0;
    if (this.nextBtn) this.nextBtn.disabled = vpos === visible.length - 1;
  }

  /* ---------- variant → media filtering ---------- */

  // Normalise for matching: lowercase, × → x, German transliteration (ß→ss,
  // ä→ae, ö→oe, ü→ue — so "Weiß" matches a "weiss" file name and ß can never
  // act as a false word boundary), separators folded to spaces, whitespace
  // collapsed. Applied identically to option values, ALT text and file names.
  _norm(s) {
    return (s || '').toLowerCase()
      .replace(/×/g, 'x')
      .replace(/ß/g, 'ss').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
      .replace(/[-_/.]+/g, ' ')
      .replace(/\s+/g, ' ').trim();
  }
  // Canonical dimension tokens: every "AxB" digit pair, smaller number first, so
  // file names shot in the other orientation ("70x50") still match "50 x 70 cm".
  _dims(s) {
    const out = [];
    const re = /(\d{2,3})\s*[x×]\s*(\d{2,3})/g;
    const str = (s || '').toLowerCase();
    let m;
    while ((m = re.exec(str))) {
      const a = parseInt(m[1], 10), b = parseInt(m[2], 10);
      const t = Math.min(a, b) + 'x' + Math.max(a, b);
      if (out.indexOf(t) === -1) out.push(t);
    }
    return out;
  }
  _escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  // All option values of THIS product, read from the variant-picker inputs that
  // main-product.liquid renders in the same .s-pdp section (radios keep every
  // value in the DOM even when combo-hidden). Each entry: normalised form,
  // canonical dims, match terms (norm + word aliases), and 1-based position.
  _knownValues() {
    const root = this.closest('.s-pdp') || document;
    const seen = {};
    const out = [];
    const add = (value, pos) => {
      if (!value) return;
      const key = pos + '::' + value;
      if (seen[key]) return;
      seen[key] = true;
      const norm = this._norm(value);
      const terms = [norm];
      norm.split(' ').forEach((w) => {
        (KK_VALUE_ALIASES[w] || []).forEach((a) => { if (terms.indexOf(a) === -1) terms.push(a); });
      });
      out.push({ norm: norm, dims: this._dims(value), pos: pos, terms: terms });
    };
    root.querySelectorAll('input[type="radio"][data-option-position]').forEach((r) => {
      add(r.value, parseInt(r.getAttribute('data-option-position'), 10) || 0);
    });
    root.querySelectorAll('select[data-option-position] option').forEach((o) => {
      const sel = o.closest('select');
      add(o.value, parseInt(sel.getAttribute('data-option-position'), 10) || 0);
    });
    return out;
  }

  // The picker's currently selected value per option position (server-checked state).
  _selectedByPosFromDOM() {
    const root = this.closest('.s-pdp') || document;
    const byPos = {};
    root.querySelectorAll('input[type="radio"][data-option-position]:checked').forEach((r) => {
      byPos[parseInt(r.getAttribute('data-option-position'), 10) || 0] = this._norm(r.value);
    });
    root.querySelectorAll('select[data-option-position]').forEach((sel) => {
      if (sel.value) byPos[parseInt(sel.getAttribute('data-option-position'), 10) || 0] = this._norm(sel.value);
    });
    return byPos;
  }

  // Tag one slide: which option values does its ALT + file name reference?
  // Returns { pos: [valueNorm, ...], ... } or null for generic media (no tags,
  // videos, 3D models) that must always stay visible.
  _tagsFor(el) {
    const type = el.getAttribute('data-media-type');
    if (type && type !== 'image') return null;
    const alt = el.getAttribute('data-media-alt') || '';
    let file = '';
    const img = el.querySelector('img');
    if (img) {
      const src = img.getAttribute('src') || img.currentSrc || '';
      file = src.split('?')[0].split('/').pop() || '';
      try { file = decodeURIComponent(file); } catch (e) { /* keep raw */ }
    }
    const text = this._norm(alt + ' ' + file);
    const textDims = this._dims(alt + ' ' + file);
    const hits = [];
    for (let i = 0; i < (this._values || []).length; i++) {
      const v = this._values[i];
      if (v.dims.length) {
        // Size-like value: every canonical dim must appear. Dim tokens that equal
        // no option value (700x700 resolution markers) simply never get here.
        // "2er Set 50x70" and "50x70" intentionally share the 50x70 tag: set
        // selections must still show the single-pillow size photos.
        if (v.dims.every((d) => textDims.indexOf(d) !== -1)) hits.push({ v: v, byText: false });
      } else {
        // Colour / cover type: whole-word match on the value or any of its
        // English aliases (files are often named KK-Cotton-Blue etc.).
        const hit = (v.terms || [v.norm]).some((t) =>
          t.length >= 3 &&
          new RegExp('(^|[^a-z0-9ßà-ÿ])' + this._escRe(t) + '($|[^a-z0-9ßà-ÿ])').test(text));
        if (hit) hits.push({ v: v, byText: true });
      }
    }
    // Specificity pruning: when a compound value matched the text ("weiss grau"),
    // drop its component matches ("weiss", "grau") at the same position so the
    // image only shows under the compound colour — like the old exact-alt rule.
    const pruned = hits.filter((h) => {
      if (!h.byText) return true;
      return !hits.some((o) => o.byText && o !== h && o.v.pos === h.v.pos &&
        o.v.norm.length > h.v.norm.length &&
        (' ' + o.v.norm + ' ').indexOf(' ' + h.v.norm + ' ') !== -1);
    });
    if (!pruned.length) return null;
    const tags = {};
    pruned.forEach((h) => { (tags[h.v.pos] = tags[h.v.pos] || []).push(h.v.norm); });
    return tags;
  }

  // Filter slides + thumbs to the selection ({position: selectedValueNorm}).
  // A position only discriminates when its selected value has >=1 tagged slide;
  // a tagged slide stays visible only if every discriminating position it is
  // tagged on has its selected value among the slide's tags. Untagged slides
  // always show. Returns true when at least one slide is hidden.
  _applyVariantFilter(byPos) {
    if (!this._slideTags || !byPos) return false;
    if (!Object.keys(byPos).length) return false;
    let taggedTotal = 0;
    // Which positions have dedicated imagery for the CURRENT selection?
    const activePos = {};
    this.slides.forEach((s, i) => {
      const tags = this._slideTags[i];
      if (!tags) return;
      taggedTotal++;
      for (const pos in tags) {
        if (byPos[pos] && tags[pos].indexOf(byPos[pos]) !== -1) activePos[pos] = true;
      }
    });
    if (!taggedTotal) return false; // nothing tagged on this product — leave as-is
    const vis = this.slides.map((s, i) => {
      const tags = this._slideTags[i];
      if (!tags) return true;
      for (const pos in tags) {
        if (activePos[pos] && byPos[pos] && tags[pos].indexOf(byPos[pos]) === -1) return false;
      }
      return true;
    });
    // No change? Don't reset scroll/active (e.g. Bezugart switch on colour-tagged media).
    const changed = this.slides.some((s, i) => s.hidden === vis[i]);
    if (!changed) return vis.indexOf(false) !== -1;
    this.slides.forEach((s, i) => {
      s.hidden = !vis[i];
      if (this.thumbs[i]) this.thumbs[i].hidden = !vis[i];
    });
    // Keep the active slide when it survived; otherwise reset to the first
    // visible one. Either way prev/next disabled states are recomputed.
    if (vis[this._active]) {
      this._setActive(this._active, false);
    } else {
      const first = this._visibleIndexes()[0];
      if (first != null) {
        this._setActive(first, false);
        if (this.track) this.track.scrollLeft = 0;
      }
    }
    if (this._syncThumbsNext) this._syncThumbsNext();
    return vis.indexOf(false) !== -1;
  }
}

if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
